import type { StoreAnalyticsOut } from "../redux/services/store/storeSettingsApi";

declare global {
  interface MetaPixelFn {
    (...args: unknown[]): void;
    callMethod?: (...args: unknown[]) => void;
    queue?: unknown[][];
    loaded?: boolean;
    version?: string;
    push?: MetaPixelFn;
  }

  interface Window {
    dataLayer?: Array<Record<string, unknown> | unknown[]>;
    gtag?: (...args: unknown[]) => void;
    fbq?: MetaPixelFn;
    _fbq?: MetaPixelFn;
  }
}

const loadedGtmIds = new Set<string>();
const loadedGaScriptIds = new Set<string>();
const configuredGaIds = new Set<string>();
const configuredMetaPixelIds = new Set<string>();

const getTrimmedId = (value?: string | null): string | null => {
  const cleaned = String(value || "").trim();
  return cleaned ? cleaned : null;
};

const ensureDataLayer = () => {
  if (typeof window === "undefined") {
    return null;
  }

  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

const ensureHeadScript = (id: string, src: string) => {
  if (typeof document === "undefined") {
    return;
  }

  if (document.getElementById(id)) {
    return;
  }

  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
};

const ensureGtmLoaded = (containerId: string) => {
  if (loadedGtmIds.has(containerId)) {
    return;
  }

  const dataLayer = ensureDataLayer();
  if (!dataLayer) {
    return;
  }

  dataLayer.push({
    "gtm.start": Date.now(),
    event: "gtm.js",
  });

  ensureHeadScript(
    `fz-gtm-${containerId}`,
    `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`
  );
  loadedGtmIds.add(containerId);
};

const ensureGaLoaded = (measurementId: string) => {
  const dataLayer = ensureDataLayer();
  if (!dataLayer) {
    return;
  }

  if (!loadedGaScriptIds.has(measurementId)) {
    ensureHeadScript(
      `fz-ga-${measurementId}`,
      `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
    );
    loadedGaScriptIds.add(measurementId);
  }

  if (!window.gtag) {
    window.gtag = (...args: unknown[]) => {
      const currentDataLayer = ensureDataLayer();
      currentDataLayer?.push(args);
    };
  }

  if (!configuredGaIds.has(measurementId)) {
    window.gtag("js", new Date());
    window.gtag("config", measurementId, { send_page_view: false });
    configuredGaIds.add(measurementId);
  }
};

const ensureMetaPixelBase = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  if (window.fbq) {
    return;
  }

  const fbq = ((...args: unknown[]) => {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
      return;
    }

    fbq.queue = fbq.queue || [];
    fbq.queue.push(args);
  }) as MetaPixelFn;

  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];

  window.fbq = fbq;
  if (!window._fbq) {
    window._fbq = fbq;
  }

  ensureHeadScript(
    "fz-meta-pixel-base",
    "https://connect.facebook.net/en_US/fbevents.js"
  );
};

const ensureMetaPixelLoaded = (pixelId: string) => {
  ensureMetaPixelBase();
  if (!window.fbq || configuredMetaPixelIds.has(pixelId)) {
    return;
  }

  window.fbq("init", pixelId);
  configuredMetaPixelIds.add(pixelId);
};

const resolveAnalyticsIds = (analytics?: StoreAnalyticsOut | null) => ({
  enabled: !!analytics?.enabled,
  gtmContainerId: getTrimmedId(analytics?.gtm_container_id),
  gaMeasurementId: getTrimmedId(analytics?.ga_measurement_id),
  metaPixelId: getTrimmedId(analytics?.meta_pixel_id),
});

export const initializeStorefrontAnalytics = (analytics?: StoreAnalyticsOut | null) => {
  const { enabled, gtmContainerId, gaMeasurementId, metaPixelId } = resolveAnalyticsIds(analytics);
  if (!enabled) {
    return;
  }

  if (gtmContainerId) {
    ensureGtmLoaded(gtmContainerId);
  } else if (gaMeasurementId) {
    ensureGaLoaded(gaMeasurementId);
  }

  if (metaPixelId) {
    ensureMetaPixelLoaded(metaPixelId);
  }
};

export const trackStorefrontPageView = ({
  analytics,
  pagePath,
  pageLocation,
  pageTitle,
}: {
  analytics?: StoreAnalyticsOut | null;
  pagePath: string;
  pageLocation: string;
  pageTitle: string;
}) => {
  const { enabled, gtmContainerId, gaMeasurementId, metaPixelId } = resolveAnalyticsIds(analytics);
  if (!enabled) {
    return;
  }

  if (gtmContainerId) {
    ensureGtmLoaded(gtmContainerId);
    const dataLayer = ensureDataLayer();
    dataLayer?.push({
      event: "page_view",
      page_path: pagePath,
      page_location: pageLocation,
      page_title: pageTitle,
    });
  } else if (gaMeasurementId) {
    ensureGaLoaded(gaMeasurementId);
    window.gtag?.("event", "page_view", {
      page_path: pagePath,
      page_location: pageLocation,
      page_title: pageTitle,
    });
  }

  if (metaPixelId) {
    ensureMetaPixelLoaded(metaPixelId);
    window.fbq?.("track", "PageView");
  }
};

export const trackStorefrontEvent = ({
  analytics,
  eventName,
  params = {},
}: {
  analytics?: StoreAnalyticsOut | null;
  eventName: string;
  params?: Record<string, unknown>;
}) => {
  const { enabled, gtmContainerId, gaMeasurementId, metaPixelId } = resolveAnalyticsIds(analytics);
  if (!enabled) {
    return;
  }

  if (gtmContainerId) {
    ensureGtmLoaded(gtmContainerId);
    const dataLayer = ensureDataLayer();
    dataLayer?.push({
      event: eventName,
      ...params,
    });
  } else if (gaMeasurementId) {
    ensureGaLoaded(gaMeasurementId);
    window.gtag?.("event", eventName, params);
  }

  if (metaPixelId) {
    ensureMetaPixelLoaded(metaPixelId);
    window.fbq?.("trackCustom", eventName, params);
  }
};
